// src/utils/rankingCalculator.ts



// ── Category Display Mapping ──────────────────────────────────────────────────
// Firestore stores "Pin" and "Fin" — these map to user-facing "Kids A" / "Kids B".
// Use categoryDisplayLabel() everywhere a category value is shown to a user.

export const CATEGORY_DISPLAY_MAP: Record<string, string> = {
  Pin: "Kids A",
  Fin: "Kids B",
};

/**
 * Returns the user-facing label for a stored category value.
 * "Pin" → "Kids A", "Fin" → "Kids B", anything else → unchanged.
 */
export function categoryDisplayLabel(type: string): string {
  return CATEGORY_DISPLAY_MAP[type] ?? type;
}

// ── Competition Type Labels ───────────────────────────────────────────────────

export const COMPETITION_TYPE_LABELS: Record<string, string> = {
  georgian_championship: "Georgian Championship",
  regional_open_major: "Regional Open (Major)",
  regional_open: "Regional Open (Standard)",
  league: "League",
  international_g1: "International G1",
  international_g2: "International G2",
  international_g4: "International G4",
  international_g12: "International G12",
  custom: "Custom",
};

// ── Points Table ─────────────────────────────────────────────────────────────
// place tiers: 1st, 2nd, 3rd, 5-8 (semi-final loss), 9-16, 17+ (participation)

interface PointRow {
  first: number;
  second: number;
  third: number;
  fifth: number;   // 5th–8th place (semi-final losers)
  ninth: number;   // 9th–16th place
  participation: number;
  perWin: number;
}

const POINTS_TABLE: Record<string, PointRow> = {
  georgian_championship: { first: 32, second: 16, third: 8,  fifth: 4,  ninth: 2,  participation: 1, perWin: 2 },
  regional_open_major:   { first: 24, second: 12, third: 6,  fifth: 3,  ninth: 2,  participation: 1, perWin: 1 },
  regional_open:         { first: 16, second: 8,  third: 4,  fifth: 2,  ninth: 1,  participation: 1, perWin: 1 },
  league:                { first: 8,  second: 4,  third: 2,  fifth: 1,  ninth: 1,  participation: 1, perWin: 0 },
  international_g1:      { first: 10, second: 5,  third: 3,  fifth: 1,  ninth: 1,  participation: 1, perWin: 0 },
  international_g2:      { first: 20, second: 10, third: 6,  fifth: 2,  ninth: 1,  participation: 1, perWin: 0 },
  international_g4:      { first: 40, second: 20, third: 10, fifth: 4,  ninth: 2,  participation: 1, perWin: 0 },
  international_g12:     { first: 120,second: 60, third: 30, fifth: 10, ninth: 5,  participation: 2, perWin: 0 },
  custom:                { first: 0,  second: 0,  third: 0,  fifth: 0,  ninth: 0,  participation: 0, perWin: 0 },
};

// ── Place Ranges ─────────────────────────────────────────────────────────────

export interface PlaceRange {
  label: string;   // Display in UI, e.g. "5th–8th"
  value: number;   // Canonical numeric place stored in Firestore (1, 2, 3, 5, 9, 17)
}

export const PLACE_RANGES: PlaceRange[] = [
  { label: "1st",      value: 1  },
  { label: "2nd",      value: 2  },
  { label: "3rd",      value: 3  },
  { label: "5th–8th",  value: 5  },
  { label: "9th–16th", value: 9  },
  { label: "17th+",    value: 17 },
];

/**
 * Maps any numeric place to the canonical tier value used by the points table.
 * e.g. 6 → 5 (5th–8th tier), 12 → 9 (9th–16th tier), 20 → 17 (17th+ tier)
 */
export function placeToTier(place: number | null): number | null {
  if (place === null) return null;
  if (place === 1) return 1;
  if (place === 2) return 2;
  if (place === 3) return 3;
  if (place >= 4 && place <= 8)  return 5;
  if (place >= 9 && place <= 16) return 9;
  return 17;
}

/**
 * Returns the display label for a stored place value.
 */
export function placeLabel(place: number | null): string {
  if (place === null) return "Participated";
  const found = PLACE_RANGES.find((r) => r.value === placeToTier(place));
  return found ? found.label : `#${place}`;
}

// ── Points Calculation ───────────────────────────────────────────────────────

function getPointsForTier(row: PointRow, tier: number | null): number {
  if (tier === null)  return row.participation;
  if (tier === 1)     return row.first;
  if (tier === 2)     return row.second;
  if (tier === 3)     return row.third;
  if (tier === 5)     return row.fifth;
  if (tier === 9)     return row.ninth;
  return row.participation;
}

/**
 * Returns the default points configuration for a given competition type.
 */
export function getDefaultPointsForType(type: string): {
  totalPoints: number;
  participationPoints: number;
  pointsPerWin: number;
} {
  const row = POINTS_TABLE[type] ?? POINTS_TABLE.custom;
  return {
    totalPoints: row.first,
    participationPoints: row.participation,
    pointsPerWin: row.perWin,
  };
}

/**
 * Calculates total points for an athlete result.
 *
 * Formula: placementPoints + participationPoints + (wins × pointsPerWin)
 * Participation points are ALWAYS added on top of placement points.
 * For "participated only" (place = null), placementPoints = 0.
 *
 * @param competitionType      e.g. "georgian_championship"
 * @param place                1, 2, 3, 5 (5-8), 9 (9-16), 17 (17+), or null (participation only)
 * @param wins                 number of fights won (for perWin bonus)
 * @param customOverride       if set, returns this value directly (admin override)
 * @param participationOverride  if set, uses this instead of the table's participation value
 */
export function calculatePoints(
  competitionType: string,
  place: number | null,
  wins: number,
  customOverride?: number,
  participationOverride?: number
): number {
  if (customOverride !== undefined) return customOverride;
  const row = POINTS_TABLE[competitionType] ?? POINTS_TABLE.custom;
  const tier = placeToTier(place);
  // Base points for a specific placement — 0 when only participated
  const basePts = tier !== null ? getPointsForTier(row, tier) : 0;
  // Participation points are always added (either from competition override or table default)
  const participPts = participationOverride !== undefined ? participationOverride : row.participation;
  const winPts = wins * row.perWin;
  return basePts + participPts + winPts;
}

// ── Age Group Definitions ─────────────────────────────────────────────────────

export interface AgeGroupDef {
  key: string;          // stored in Firestore as ageGroup
  label: string;        // display label
  birthYearRange?: string; // informational, e.g. "2017–2019"
}

export const AGE_GROUP_DEFINITIONS: AgeGroupDef[] = [
  // ── Kids / PIN (youngest, born 2017–2019) ──────────────────────────────────
  // NOTE: key must stay "Kids" — existing Firestore docs use this value.
  { key: "Kids",    label: "Kids A", birthYearRange: "2017–2019" },

  // ── Fin (born 2015–2016) ───────────────────────────────────────────────────
  // NEW in Phase 12: second kids age bracket with heavier weight divisions.
  { key: "Fin",     label: "Kids B", birthYearRange: "2015–2016" },

  { key: "Cadets",  label: "Cadets",  birthYearRange: "2012–2014" },
  { key: "Juniors", label: "Juniors", birthYearRange: "2009–2011" },
  { key: "U21",     label: "U21",     birthYearRange: "2006–2010" },
  { key: "Seniors", label: "Seniors", birthYearRange: "2009 or before" },
  
];

// Flat list of keys for simple iterations
export const AGE_GROUPS: string[] = AGE_GROUP_DEFINITIONS.map((g) => g.key);

// ── Weight Classes Per Age Group ──────────────────────────────────────────────

interface GenderedWeights { male: string[]; female: string[] }

export const WEIGHT_CLASSES_BY_GROUP: Record<string, GenderedWeights> = {
  // Pin (Kids) — born 2017–2019
  Kids: {
    male:   ["-21kg","-23kg","-25kg","-27kg","-29kg","-31kg","-33kg","-36kg","-40kg","+40kg"],
    female: ["-21kg","-23kg","-25kg","-27kg","-29kg","-31kg","-33kg","-36kg","-40kg","+40kg"],
  },

  // Fin — born 2015–2016 (heavier division than Pin)
  Fin: {
    male:   ["-27kg","-30kg","-33kg","-36kg","-40kg","-44kg","-48kg","-52kg","-57kg","+57kg"],
    female: ["-27kg","-30kg","-33kg","-36kg","-40kg","-44kg","-48kg","-52kg","-57kg","+57kg"],
  },

  Cadets: {
    male:   ["-33kg","-37kg","-41kg","-44kg","-49kg","-53kg","-57kg","-61kg","-65kg","+65kg"],
    female: ["-29kg","-33kg","-37kg","-41kg","-44kg","-47kg","-51kg","-55kg","-59kg","+59kg"],
  },
  Juniors: {
    male:   ["-45kg","-48kg","-51kg","-55kg","-59kg","-63kg","-68kg","-73kg","-78kg","+78kg"],
    female: ["-42kg","-44kg","-46kg","-49kg","-52kg","-55kg","-59kg","-63kg","-68kg","+68kg"],
  },
  U21: {
    male:   ["-54kg","-58kg","-63kg","-68kg","-74kg","-80kg","-87kg","+87kg"],
    female: ["-46kg","-49kg","-53kg","-57kg","-62kg","-67kg","-73kg","+73kg"],
  },
  Seniors: {
    male:   ["-54kg","-58kg","-63kg","-68kg","-74kg","-80kg","-87kg","+87kg"],
    female: ["-46kg","-49kg","-53kg","-57kg","-62kg","-67kg","-73kg","+73kg"],
  },
  
};

/**
 * Returns weight class options for a given age group and gender.
 * Falls back to Seniors weights if group not found.
 */
export function getWeightClassesForGroup(ageGroup: string, gender: string): string[] {
  const group = WEIGHT_CLASSES_BY_GROUP[ageGroup] ?? WEIGHT_CLASSES_BY_GROUP.Seniors;
  const isFemale = gender === "female" || gender === "para_female";
  return isFemale ? group.female : group.male;
}

// Legacy exports for backward compatibility ─────────────────────────────────
export const WEIGHT_CLASSES_MALE   = WEIGHT_CLASSES_BY_GROUP.Seniors.male;
export const WEIGHT_CLASSES_FEMALE = WEIGHT_CLASSES_BY_GROUP.Seniors.female;

// ── Rank Assignment ───────────────────────────────────────────────────────────

export interface AthletePoints {
  athleteId: string;
  firstName: string;
  lastName: string;
  club: string;
  currentYearPoints: number;
  previousYearPoints: number;
}

export interface RankedAthlete extends AthletePoints {
  rank: number;
  nation: "GEO";
}

/**
 * Sorts athletes by currentYearPoints (desc), then previousYearPoints (desc),
 * and assigns rank numbers with tie support.
 */
export function assignRanks(athletes: AthletePoints[]): RankedAthlete[] {
  const sorted = [...athletes].sort((a, b) => {
    if (b.currentYearPoints !== a.currentYearPoints)
      return b.currentYearPoints - a.currentYearPoints;
    return b.previousYearPoints - a.previousYearPoints;
  });

  const ranked: RankedAthlete[] = [];
  let currentRank = 1;
  for (let i = 0; i < sorted.length; i++) {
    if (
      i > 0 &&
      (sorted[i].currentYearPoints !== sorted[i - 1].currentYearPoints ||
        sorted[i].previousYearPoints !== sorted[i - 1].previousYearPoints)
    ) {
      currentRank = i + 1;
    }
    ranked.push({ ...sorted[i], rank: currentRank, nation: "GEO" });
  }
  return ranked;
}